import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  SafeAreaView,
  Dimensions,
  Image,
  ScrollView,
  Platform,
  ActivityIndicator,
  Keyboard,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import LoadingButton from '../components/LoadingButton';
import { BottomSheetModal, BottomSheetScrollView } from '../components/BottomSheet';
import type { BottomSheetModalRef } from '../components/BottomSheet';
import GoogleAuthButton from '../components/GoogleAuthButton';
import SignInFieldsOnly from './SignInFieldsOnly';
import { getIpAddressFunc } from '../components/common/utils/ip-address';
import { createUserSession } from '../models/user-session-model';
import {
  getDriverPlan,
  loginUser,
  validateUserToken,
  getActiveCharges,
} from '../store/actions/user-actions';
import { useDispatch } from 'react-redux';
import { userActions } from '../store/slices/user-slice';
import { storeUserSession } from '../services/helpers/session-helpers';
import { AppDispatch } from '../store';
import { setSession } from '../store/actions/user-session';
// @ts-ignore
import AppleAuthButton from '../components/AppleAuthButton/AppleAuthButton';
import { reassignOrder } from '../services/orders-service';
import { OrderAttributes } from '../models/order-model';
import GoogleAuthButtonMobile from '../components/GoogleAuthButtonMobile';
import { getUserInfo } from '../services/user-service';
import { notificationService } from '../services/notification-service';
import { useMenu } from '../navigation/MenuContext';
//import appleAuth from "@invertase/react-native-apple-authentication";

const { width } = Dimensions.get('window');

interface SignInOptionsProps {
  navigation: any; // Update with your specific navigation type if using react-navigation typings
  bottomSheetModalRef: React.RefObject<BottomSheetModalRef>;
  destination: string;
  orderId?: string; // Optional orderId prop
  setNewOrder?: (newOrder: OrderAttributes | null) => void;
  transferOrderOnSignIn?: boolean; // For ChargeSummary: transfer completed order after sign-in
}

const SignInOptions: React.FC<SignInOptionsProps> = ({
  navigation,
  bottomSheetModalRef,
  destination,
  orderId,
  setNewOrder,
  transferOrderOnSignIn = false,
}) => {
  const [signInWithEmail, setSignInWithEmail] = useState<boolean>(false);
  const [keyboardVisible, setKeyboardVisible] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const { openMenu } = useMenu();
  // Ref for the ScrollView
  const scrollViewRef = useRef<any>(null);
  // Ref for the SignInFieldsOnly container
  const signInFieldsRef = useRef<View>(null);
  
  // Define snap points for bottom sheet
  const snapPoints = useMemo(() => ['60%', '90%'], []);

  // Safely get navigation state - may not be available if rendered before navigation is ready
  const state = navigation?.getState?.();
  const currentScreen = state?.routes?.[state?.index]?.name ?? '';

  const handleSignInWithEmail = () => {
    const newValue = !signInWithEmail;
    setSignInWithEmail(newValue);
    
    // Note: Web implementation (RBSheet) doesn't support dynamic snap points
    // Native implementation will handle this automatically via snapPoints prop
  };

  const handleSignUpPress = () => {
    bottomSheetModalRef.current?.dismiss();
    navigation.navigate('CreateAccount', { formEmail: '' });
  };

  const handleSignInSubmit = async (
    email: string,
    password: string,
    logintype: string
  ) => {
    // ChargeSummary flow: Preserve guest token for transferOrder BEFORE login
    let guestToken: string | null = null;
    if (transferOrderOnSignIn && orderId) {
      guestToken = await AsyncStorage.getItem('token');
      if (!guestToken) {
        console.warn('No guest token found - guest may not be authenticated for transferOrder');
      } else {
        console.log('Guest token preserved for transferOrder after sign-in');
      }
    }

    if(logintype !== 'apple') {
      const ipAddress = await getIpAddressFunc();

      const loginObj = createUserSession({
        email: email,
        password: password,
        username: email,
        IpAddress: ipAddress,
        logintype: logintype,
        service: logintype,
        sub: password,
      });

      // Await login and check for failure
      // NOTE: loginUser() clears AsyncStorage, removing the guest token
      const loginResp = await loginUser(loginObj);
      if (!loginResp) {
        // loginUser already shows a notification, but you can add another if you want
        notificationService.sendNotification('error', 'Login failed: Invalid credentials.');
        return; // Do not proceed or close the sheet
      }
    }

    // Only proceed if login succeeded
    const getSessionData = await validateUserToken();
    const newSessionData = getSessionData.payload;
    if (
      newSessionData &&
      newSessionData.access_token
    ) {
      await storeUserSession(newSessionData);
      dispatch(getDriverPlan());
      
      // Fetch active charges after successful login
      dispatch(getActiveCharges());
      
      // Handle order assignment based on context
      if (orderId && newSessionData.id) {
        if (transferOrderOnSignIn) {
          // ChargeSummary flow: Transfer completed order to newly signed-in user
          // NOTE: This happens AFTER login, so we use the new user's auth token
          // The guest token was already replaced by loginUser()
          console.log('Transferring completed order to signed-in user:', orderId);
          console.log('Using new user auth token for transfer (post-login)');
          
          // We need to temporarily restore the guest token to transfer the order
          if (guestToken) {
            console.log('Restoring guest token temporarily for transfer');
            await AsyncStorage.setItem('token', guestToken);
            
            try {
              const { transferOrder } = require('../services/orders-service');
              const transferResult = await transferOrder(orderId, newSessionData.id);
              if (transferResult && transferResult.payload) {
                console.log('Order transferred successfully');
              } else {
                console.warn('Order transfer may have failed');
              }
            } catch (transferError) {
              console.error('Error transferring order:', transferError);
            }
            
            // Restore the new user's token
            console.log('Restoring new user token after transfer');
            await AsyncStorage.setItem('token', newSessionData.access_token);
          } else {
            console.error('Cannot transfer order: guest token was not preserved');
            notificationService.sendNotification(
              'warning',
              'Could not transfer order history. Please contact support if needed.'
            );
          }
        } else if (setNewOrder) {
          // ChargerStation flow: Reassign active order and update payment sheet
          console.log('Reassigning active order to signed-in user:', orderId);
          const newOrder = await reassignOrder(orderId);
          if (newOrder && newOrder.payload) {
            setNewOrder(newOrder.payload);
          }
        }
      }
      
      bottomSheetModalRef.current?.dismiss();
      
      // Fetch and set user info
      const userInfoResp = await getUserInfo();
      if (userInfoResp && userInfoResp.payload && userInfoResp.payload.user) {
        dispatch(
          userActions.setUserObj({ userObj: userInfoResp.payload.user })
        );
      }
      dispatch(setSession(newSessionData));
      
      // Don't navigate if already on the destination screen (e.g., ChargeSummary)
      // Just close the bottom sheet and stay on current screen
      if (destination === 'ChargeSummary') {
        console.log('Already on ChargeSummary, staying on current screen after sign-in');
        // No navigation needed - user stays on ChargeSummary with their completed transaction
      } else if (destination === 'MyAccount') {
        // Open the side menu instead of navigating to MyAccount screen
        // For hosts, navigate to MyChargers; for drivers, open menu
        if (newSessionData.primary_use === 'host') {
          navigation.navigate('MyChargers', { myLocation: null });
        } else {
          // Open side menu for non-host users
          openMenu();
        }
      } else {
        // Navigate to other destinations (MyChargers, etc.)
        navigation.navigate(destination, { myLocation: null });
      }
    } else {
      notificationService.sendNotification(
        'error',
        'Login failed: session data incomplete.'
      );
    }
  };

  // Effect to scroll when signInWithEmail becomes true
  useEffect(() => {
    if (signInWithEmail) {
      // Delay to ensure the layout has updated and bottom sheet has expanded
      setTimeout(() => {
        // Scroll to the end to show the sign-in fields
        if (scrollViewRef.current) {
          scrollViewRef.current.scrollToEnd({ animated: true });
        }
      }, 400);
    }
  }, [signInWithEmail]);

  // Effect to handle keyboard events
  useEffect(() => {
    if (Platform.OS === 'web') return; // Skip keyboard handling on web

    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      () => {
        setKeyboardVisible(true);
        // When keyboard shows, scroll to bottom to ensure button is visible
        setTimeout(() => {
          if (scrollViewRef.current && signInWithEmail) {
            scrollViewRef.current.scrollToEnd({ animated: true });
          }
        }, 100);
      }
    );

    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => {
        setKeyboardVisible(false);
      }
    );

    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, [signInWithEmail]);

  const clearSheetState = () => {
    setSignInWithEmail(false);
  };

  const ParentView = Platform.OS === 'web' ? SafeAreaView : View;

  return (
    <ParentView>
      <BottomSheetModal
        ref={bottomSheetModalRef}
        index={0}
        snapPoints={snapPoints}
        enablePanDownToClose={true}
        onDismiss={clearSheetState}
        backgroundStyle={{
          borderRadius: 20,
          backgroundColor: '#1A1D21',
        }}
        handleIndicatorStyle={{
          backgroundColor: '#ccc',
          width: 40,
          height: 5,
        }}
        style={Platform.OS === 'web' ? {
          maxWidth: 430,
          marginLeft: 'auto',
          marginRight: 'auto',
        } : {}}
      >
        <BottomSheetScrollView
          ref={scrollViewRef}
          contentContainerStyle={{
            flexGrow: 1,
            paddingBottom: signInWithEmail && keyboardVisible 
              ? 300 // Extra padding when keyboard is visible with email sign-in
              : currentScreen === 'WelcomeScreen' ? 10 : 70,
          }}
          keyboardShouldPersistTaps="handled"
        >
          <View className="p-4">
            {Platform.OS === 'web' ? (
              <GoogleAuthButton
                navigation={navigation}
                RBRef={bottomSheetModalRef}
                destination={destination}
              />
            ) : (
              <GoogleAuthButtonMobile
                navigation={navigation}
                RBRef={bottomSheetModalRef}
                destination={destination}
                onSubmit={handleSignInSubmit}
              />
            )}

            {Platform.OS === "web" ? (
              <AppleAuthButton RBRef={bottomSheetModalRef} />
            ) : (
              <AppleAuthButton RBRef={bottomSheetModalRef} onSubmit={handleSignInSubmit} />
            )}

            <Text
              className="my-4 text-lg font-normal text-center"
              style={styles.textColor}
            >
              or
            </Text>

            <Pressable
              onPress={handleSignInWithEmail}
              style={{
                backgroundColor: '#39BEB7',
                borderRadius: 9999,
                padding: 16,
              }}
            >
              <Text style={{ fontSize: 16, fontWeight: '600', color: 'white', textAlign: 'center' }}>
                Log in with email
              </Text>
            </Pressable>

            {signInWithEmail && (
              <View
                ref={signInFieldsRef}
                style={{ width: '100%', marginTop: 10 }}
              >
                <SignInFieldsOnly onSubmit={handleSignInSubmit} autoFocus={true} />
              </View>
            )}

            <View
              style={{
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'center',
                marginTop: 10,
              }}
              className="flex-row gap-3 justify-center mt-2"
            >
              <Text style={styles.textColor} className="text-sm font-normal">
                Don't have an account?
              </Text>
              <Text
                style={styles.textColor1}
                className="text-sm font-bold"
                onPress={handleSignUpPress}
              >
                Sign up
              </Text>
            </View>
          </View>
        </BottomSheetScrollView>
      </BottomSheetModal>
    </ParentView>
  );
};

const styles = StyleSheet.create({
  containerBgColor: {
    backgroundColor: '#1A1D21',
  },
  textColor: {
    color: '#8D8E90',
  },
  textColor1: {
    color: '#39BEB7',
  },
  textColorWhite: {
    color: 'white',
  },
  bgColor: {
    backgroundColor: '#39BEB7',
  },
});

export default SignInOptions;
